/**
 * Lecture d'un code de bon / carte cadeau saisi ou scanné au comptoir (scanner web, douchette, saisie
 * manuelle, scanner natif de l'app) ou scanné par le client dans le tunnel de réservation. Source
 * unique partagée : le backend normalise le code reçu par `GET /app/voucher/code/:code` avec la même
 * fonction que le front.
 *
 * Pur, sans dépendance et sans `URL` (Hermes ne l'implémente pas entièrement) : importable depuis
 * l'app mobile via `@cfixe-monorepo/shared/lib/voucher-code`.
 */

/** Paramètre de requête qui porte le code dans le QR des cartes cadeaux (`/book/{slug}?code=…`). */
export const VOUCHER_CODE_QUERY_PARAM = 'code';
/** Paramètre de requête des liens de campagne qui pré-remplissent un code promo (`/book/{slug}?promo=…`). */
export const PROMO_CODE_QUERY_PARAM = 'promo';

/**
 * Codes acceptés après normalisation. Volontairement plus large que le format généré en V2
 * (5 groupes de 5 caractères sans 0/1/I/L/O) : les codes migrés de la V1 sont copiés tels quels
 * (ex. `06493-2CRJ4-R53BT-KYJQV-B2CRZ`) et la seed E2E utilise `E2E-GIFT-0001`. 30 = taille de
 * `voucher.unique_code`. La liste blanche protège aussi l'appel : le client Orval insère le code
 * dans le chemin sans l'encoder.
 */
const VOUCHER_CODE_PATTERN = /^[A-Z0-9-]{1,30}$/;
/** Code saisi sans tirets : 25 caractères, regroupés par 5 comme les codes générés. */
const UNDASHED_CODE_PATTERN = /^[A-Z0-9]{25}$/;
/** Au-delà, ce n'est ni un code ni une URL de carte cadeau : on ne parse pas. */
const MAX_SCAN_LENGTH = 2048;
/**
 * Code promo après mise en majuscules : alphabet de `PROMO_CODE_PATTERN` (saisie du pro) et 64
 * caractères max. Le backend compare les codes promo en majuscules.
 */
const PROMO_CODE_PATTERN = /^[A-Z0-9_-]{1,64}$/;

const URL_PATTERN = /^https?:\/\/[^/?#\s]+(\/[^?#\s]*)?(\?[^#\s]*)?(#\S*)?$/i;
const BOOK_PATH_PATTERN = /^\/book\/([^/]+)\/?$/;
/** PDF public d'une carte cadeau (`giftCardPdfPublicPath`) : ne contient pas le code. */
const GIFT_CARD_PDF_PATH_PATTERN = /\/public\/gift-card\/voucher\/[0-9a-f-]{36}\/pdf\/?$/i;

export type VoucherScanResult =
  /** `slug` : slug lu dans une URL de carte cadeau, pour orienter le pro si le bon est introuvable. */
  | { kind: 'code'; code: string; slug?: string }
  /** QR d'un PDF de carte cadeau sans code (repli des pros sans slug) : le pro doit saisir le code imprimé. */
  | { kind: 'gift_card_pdf' }
  | { kind: 'unrecognized' };

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
};

/**
 * Normalise un code saisi : majuscules, suites d'espaces ou de tirets ramenées à un tiret, tirets
 * réinsérés pour un code de 25 caractères collés. Renvoie `null` si le résultat n'est pas un code
 * acceptable (caractère interdit, trop long, vide).
 */
export function normalizeVoucherCode(raw: string): string | null {
  const collapsed = raw
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const code = UNDASHED_CODE_PATTERN.test(collapsed) ? (collapsed.match(/.{5}/g) ?? []).join('-') : collapsed;
  return VOUCHER_CODE_PATTERN.test(code) ? code : null;
}

const readQueryParam = (query: string, name: string): string | null => {
  for (const pair of query.replace(/^\?/, '').split('&')) {
    const separatorIndex = pair.indexOf('=');
    const key = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    if (safeDecode(key) === name) {
      return separatorIndex === -1 ? '' : safeDecode(pair.slice(separatorIndex + 1));
    }
  }
  return null;
};

/**
 * Interprète le texte lu par un scanner (ou collé dans le champ de saisie) :
 * - une URL de carte cadeau `…/book/{slug}?code=…` → le code (hôte et slug ignorés : ils varient
 *   selon l'environnement et le slug peut avoir été renommé depuis l'impression) ;
 * - l'URL publique du PDF d'une carte cadeau → `gift_card_pdf` (aucun code dedans) ;
 * - un code brut → le code normalisé ;
 * - tout le reste → `unrecognized`. Une URL scannée n'est jamais ouverte.
 */
export function parseVoucherScan(text: string): VoucherScanResult {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_SCAN_LENGTH) return { kind: 'unrecognized' };

  const urlMatch = URL_PATTERN.exec(trimmed);
  if (!urlMatch) {
    const code = normalizeVoucherCode(trimmed);
    return code ? { kind: 'code', code } : { kind: 'unrecognized' };
  }

  const path = urlMatch[1] ?? '/';
  if (GIFT_CARD_PDF_PATH_PATTERN.test(path)) return { kind: 'gift_card_pdf' };

  const bookMatch = BOOK_PATH_PATTERN.exec(path);
  const rawCode = bookMatch ? readQueryParam(urlMatch[2] ?? '', VOUCHER_CODE_QUERY_PARAM) : null;
  const code = rawCode ? normalizeVoucherCode(rawCode) : null;
  if (!bookMatch || !code) return { kind: 'unrecognized' };

  const slug = safeDecode(bookMatch[1]);
  return slug ? { kind: 'code', code, slug } : { kind: 'code', code };
}

export type BookingCodeScanResult =
  | { kind: 'code'; code: string }
  /** QR d'un PDF de carte cadeau sans code : le client doit saisir le code imprimé. */
  | { kind: 'gift_card_pdf' }
  | { kind: 'unrecognized' };

/**
 * Interprète le QR scanné par le client dans le champ « Code promo ou carte cadeau » du tunnel de
 * réservation, qui accepte les deux :
 * - QR d'une carte cadeau ou code de bon brut → comme `parseVoucherScan` (slug ignoré : un code
 *   d'un autre établissement est refusé par le backend comme un code inconnu) ;
 * - lien de campagne `…/book/{slug}?promo=…` ou code promo brut → le code en majuscules ;
 * - QR du PDF d'une carte cadeau → `gift_card_pdf` ;
 * - tout le reste → `unrecognized`. Une URL scannée n'est jamais ouverte.
 */
export function parseBookingCodeScan(text: string): BookingCodeScanResult {
  const voucher = parseVoucherScan(text);
  if (voucher.kind === 'code') return { kind: 'code', code: voucher.code };
  if (voucher.kind === 'gift_card_pdf') return voucher;

  const trimmed = text.trim();
  if (trimmed.length > MAX_SCAN_LENGTH) return { kind: 'unrecognized' };
  const urlMatch = URL_PATTERN.exec(trimmed);
  const rawPromo = !urlMatch
    ? trimmed
    : BOOK_PATH_PATTERN.test(urlMatch[1] ?? '/')
      ? readQueryParam(urlMatch[2] ?? '', PROMO_CODE_QUERY_PARAM)
      : null;
  const promo = rawPromo?.trim().toUpperCase();
  return promo && PROMO_CODE_PATTERN.test(promo) ? { kind: 'code', code: promo } : { kind: 'unrecognized' };
}
